import { useState, useEffect, useCallback, useRef } from "react";
import { useApp } from "@/context/AppContext";
import ObservationCard from "@/components/ObservationCard";
import ClipAnalysisDemo from "@/components/ClipAnalysisDemo";
import { Loader2, Sparkles, Clock, Lightbulb, CalendarDays, Play, Microscope, ChevronDown, ChevronUp } from "lucide-react";
import VideoThumb from "@/components/VideoThumb";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { id: "today", label: "Today" },
  { id: "week", label: "This Week" },
  { id: "month", label: "This Month" },
];

export default function SummaryTab() {
  const [period, setPeriod] = useState("today");
  const [summary, setSummary] = useState(null);
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(null);
  const [videoClip, setVideoClip] = useState(null);
  const [showClipDemo, setShowClipDemo] = useState(false);
  const [showAllObs, setShowAllObs] = useState(false);
  const [showFullTimeline, setShowFullTimeline] = useState(false);
  const { child } = useApp();
  const pollRef = useRef(null);

  const fetchSummary = useCallback(async () => {
    if (!child?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/children/${child.id}/summaries/daily`);
      if (res.data && res.data.status === "complete") setSummary(res.data);
      else setSummary(null);
    } catch { setSummary(null); }
    setLoading(false);
  }, [child]);

  const fetchClips = useCallback(async () => {
    if (!child?.id) return;
    try {
      const res = await axios.get(`${API}/children/${child.id}/clips?limit=200`);
      setClips(res.data || []);
    } catch {}
  }, [child]);

  useEffect(() => {
    if (period === "today") { fetchSummary(); fetchClips(); }
  }, [period, fetchSummary, fetchClips]);

  // Check if generation is already in progress on mount
  useEffect(() => {
    if (!child?.id) return;
    axios.get(`${API}/children/${child.id}/generation-status`).then((r) => {
      if (r.data.status === "running") {
        setGenerating(true);
        setProgress(r.data);
        startPolling();
      } else if (r.data.status === "complete") {
        fetchSummary();
      }
    }).catch(() => {});
    // eslint-disable-next-line
  }, [child]);

  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/children/${child.id}/generation-status`);
        setProgress(res.data);
        if (res.data.status === "complete") {
          clearInterval(pollRef.current);
          setGenerating(false);
          fetchSummary();
          fetchClips();
          toast.success("Daily summary is ready!");
        } else if (res.data.status === "error") {
          clearInterval(pollRef.current);
          setGenerating(false);
          toast.error(res.data.phase || "Generation failed");
        }
      } catch {}
    }, 3000);
  }, [child, fetchSummary, fetchClips]);

  useEffect(() => { return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, []);

  const generateSummary = async () => {
    setGenerating(true);
    setProgress({ status: "running", phase: "Starting...", total_clips: 0, analyzed_clips: 0 });
    try {
      await axios.post(`${API}/children/${child.id}/generate-daily-summary`);
      startPolling();
    } catch (e) {
      setGenerating(false);
      toast.error("Could not start generation");
    }
  };

  const getClipById = (clipId) => {
    if (!clipId) return null;
    return clips.find((c) => c.id === clipId || c.id.startsWith(clipId) || clipId.startsWith(c.id));
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    try {
      const parts = ts.split("T");
      if (parts.length < 2) return ts;
      const timeParts = parts[1].split(":");
      let h = parseInt(timeParts[0]);
      const m = timeParts[1];
      const ampm = h >= 12 ? "PM" : "AM";
      if (h > 12) h -= 12;
      if (h === 0) h = 12;
      return `${h}:${m} ${ampm}`;
    } catch { return ts; }
  };

  return (
    <div className="p-4 pt-6" data-testid="summary-tab">
      {showClipDemo ? (
        <ClipAnalysisDemo onBack={() => setShowClipDemo(false)} />
      ) : (
      <>
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center gap-2">
          <div className="w-2 h-8 rounded-full bg-gradient-to-b from-[#8E9B82] to-[#D4A373]" />
          <h1 className="font-outfit text-3xl font-medium text-[#2F3630] tracking-tight">
            {child?.name ? `${child.name}'s Day` : "Today's Summary"}
          </h1>
        </div>
        <p className="font-manrope text-sm text-[#6B7264] mt-1 ml-5">
          {clips.length > 0 ? `${clips.length} camera clips received` : "Waiting for camera clips..."}
        </p>
      </div>

      {/* Clip Analysis Demo Link */}
      <button
        data-testid="open-clip-demo-btn"
        onClick={() => setShowClipDemo(true)}
        className="w-full flex items-center gap-3 bg-white rounded-[16px] p-4 border border-[#E2DFD8]/50 shadow-sm mb-5 hover:border-[#8E9B82]/40 transition-all group"
      >
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#8E9B82]/15 to-[#D4A373]/15 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
          <Microscope size={18} className="text-[#8E9B82]" strokeWidth={1.5} />
        </div>
        <div className="text-left flex-1">
          <p className="font-outfit text-sm font-medium text-[#2F3630]">See how Aavaa analyzes a clip</p>
          <p className="font-manrope text-[11px] text-[#6B7264]">Watch AI analyze a single video clip in real-time</p>
        </div>
        <Play size={14} className="text-[#8E9B82] flex-shrink-0" />
      </button>

      {/* Period Selector */}
      <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar">
        {PERIODS.map((p) => (
          <button
            key={p.id}
            data-testid={`period-${p.id}`}
            onClick={() => setPeriod(p.id)}
            className={`px-4 py-2 rounded-full text-sm font-manrope font-medium whitespace-nowrap transition-all duration-300 ${
              period === p.id
                ? "bg-gradient-to-r from-[#8E9B82] to-[#7A8A6E] text-white shadow-sm shadow-[#8E9B82]/20"
                : "bg-white text-[#6B7264] border border-[#E2DFD8] hover:border-[#8E9B82]/50 hover:shadow-sm"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Today View */}
      {period === "today" && (
        <>
          {/* Generate Button (when no summary) */}
          {!summary && !generating && !loading && (
            <div className="bg-white rounded-[24px] p-6 shadow-[0_4px_24px_-8px_rgba(47,54,48,0.05)] border border-[#E2DFD8]/50 text-center mb-6">
              <Sparkles size={32} className="text-[#D4A373] mx-auto mb-3" strokeWidth={1.5} />
              <h3 className="font-outfit text-lg font-medium text-[#2F3630] mb-2">Ready to generate today's summary</h3>
              <p className="font-manrope text-sm text-[#6B7264] mb-4">
                {clips.length > 0
                  ? `${clips.length} clips from the camera are ready to be analyzed.`
                  : "Clips will be ingested from the camera when you start."}
              </p>
              <Button
                data-testid="generate-summary-btn"
                onClick={generateSummary}
                className="rounded-full bg-gradient-to-r from-[#8E9B82] to-[#7A8A6E] hover:from-[#7A8A6E] hover:to-[#6B7B5F] text-white h-12 px-8 font-outfit font-medium text-base transition-all duration-300 hover:scale-[0.98] active:scale-95 shadow-md shadow-[#8E9B82]/20"
              >
                Generate Day's Summary
              </Button>
            </div>
          )}

          {/* Progress */}
          {generating && progress && (
            <div className="bg-white rounded-[24px] p-6 shadow-[0_4px_24px_-8px_rgba(47,54,48,0.05)] border border-[#E2DFD8]/50 text-center mb-6" data-testid="generation-progress">
              <Loader2 size={32} className="text-[#8E9B82] mx-auto mb-3 animate-spin" />
              <h3 className="font-outfit text-lg font-medium text-[#2F3630] mb-2">{progress.phase || "Processing..."}</h3>
              {progress.total_clips > 0 && (
                <>
                  <div className="w-full bg-[#E2DFD8] rounded-full h-2 mb-2">
                    <div
                      className="progress-shimmer h-2 rounded-full transition-all duration-500"
                      style={{ width: `${Math.max(2, (progress.analyzed_clips / progress.total_clips) * 100)}%` }}
                    />
                  </div>
                  <p className="font-manrope text-sm text-[#6B7264]">
                    {progress.analyzed_clips} / {progress.total_clips} clips analyzed
                  </p>
                </>
              )}
            </div>
          )}

          {/* Loading */}
          {loading && !generating && (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={24} className="text-[#8E9B82] animate-spin" />
            </div>
          )}

          {/* Summary Content */}
          {summary && (
            <div className="space-y-6 animate-fade-up">
              {/* Daily Overview */}
              <div className="bg-white rounded-[24px] p-5 shadow-[0_4px_24px_-8px_rgba(47,54,48,0.05)] border border-[#E2DFD8]/50 relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#8E9B82] via-[#D4A373] to-[#8E9B82] opacity-60" />
                <h3 className="font-outfit text-lg font-medium text-[#2F3630] mb-3 mt-1">Overview</h3>
                <p className="font-manrope text-sm text-[#6B7264] leading-relaxed">
                  {summary.daily_overview}
                </p>
              </div>

              {/* Timeline */}
              {summary.timeline?.length > 0 && (() => {
                const [showAllTimeline, setShowAllTimeline] = [showAllObs ? true : false, () => {}];
                const timelineItems = summary.timeline;
                const visibleTimeline = showFullTimeline ? timelineItems : timelineItems.slice(0, 2);
                const hiddenTimelineCount = timelineItems.length - 2;
                return (
                <div className="bg-white rounded-[24px] p-5 shadow-[0_4px_24px_-8px_rgba(47,54,48,0.05)] border border-[#E2DFD8]/50">
                  <div className="flex items-center gap-2 mb-4">
                    <Clock size={18} className="text-[#D4A373]" strokeWidth={1.5} />
                    <h3 className="font-outfit text-lg font-medium text-[#2F3630]">Timeline</h3>
                  </div>
                  <div className="space-y-0">
                    {visibleTimeline.map((item, i) => (
                      <div key={i} className="flex gap-3" data-testid={`timeline-item-${i}`}>
                        <div className="flex flex-col items-center">
                          <div className="w-2.5 h-2.5 rounded-full bg-[#8E9B82] mt-1" />
                          {(showFullTimeline ? i < timelineItems.length - 1 : i < 1) && <div className="w-0.5 flex-1 bg-[#E2DFD8] my-1" />}
                        </div>
                        <div className="pb-5 flex-1">
                          <span className="text-xs font-manrope font-medium text-[#D4A373]">{item.time_range}</span>
                          <h4 className="font-outfit text-sm font-medium text-[#2F3630]">{item.title}</h4>
                          <p className="font-manrope text-xs text-[#6B7264] leading-relaxed">{item.description}</p>
                          {item.clip_ids?.length > 0 && (
                            <div className="flex gap-1 mt-2 overflow-x-auto no-scrollbar">
                              {item.clip_ids.slice(0, 3).map((cid) => {
                                const clip = getClipById(cid);
                                return clip ? (
                                  <button key={cid} onClick={() => setVideoClip(clip)} className="w-16 h-10 rounded-lg flex-shrink-0 overflow-hidden border border-[#E2DFD8]/40 hover:shadow-sm transition-all" data-testid={`timeline-clip-${cid}`}>
                                    <VideoThumb clipId={clip.id} className="w-full h-full rounded-lg" iconSize={10} />
                                  </button>
                                ) : null;
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {!showFullTimeline && hiddenTimelineCount > 0 && (
                    <button data-testid="show-more-timeline" onClick={() => setShowFullTimeline(true)} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-[#8E9B82] font-manrope text-sm font-medium hover:bg-[#8E9B82]/5 transition-all">
                      <ChevronDown size={16} /> Show {hiddenTimelineCount} more
                    </button>
                  )}
                  {showFullTimeline && timelineItems.length > 2 && (
                    <button data-testid="show-less-timeline" onClick={() => setShowFullTimeline(false)} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-[#6B7264] font-manrope text-sm font-medium hover:bg-[#F7F5F0] transition-all">
                      <ChevronUp size={16} /> Show less
                    </button>
                  )}
                </div>
                );
              })()}

              {/* Observation Cards */}
              {summary.observation_answers?.length > 0 && (() => {
                const PINNED_KEYWORDS = ["throw glasses", "remove or throw glasses", "throw away stuff", "throw away"];
                const pinned = [];
                const rest = [];
                summary.observation_answers.forEach((obs) => {
                  const t = (obs.title || "").toLowerCase();
                  if (PINNED_KEYWORDS.some(kw => t.includes(kw))) {
                    pinned.push(obs);
                  } else {
                    rest.push(obs);
                  }
                });
                const sorted = [...pinned, ...rest];
                const visible = showAllObs ? sorted : sorted.slice(0, 2);
                const hiddenCount = sorted.length - 2;

                return (
                  <div>
                    <h3 className="font-outfit text-lg font-medium text-[#2F3630] mb-3 px-1">Observations</h3>
                    <div className="space-y-3">
                      {visible.map((obs, i) => (
                        <ObservationCard
                          key={i}
                          observation={obs}
                          clips={clips}
                          onPlayClip={setVideoClip}
                          index={i}
                        />
                      ))}
                    </div>
                    {!showAllObs && hiddenCount > 0 && (
                      <button
                        data-testid="show-more-observations"
                        onClick={() => setShowAllObs(true)}
                        className="w-full mt-3 flex items-center justify-center gap-2 py-3 rounded-2xl bg-white border border-[#E2DFD8]/50 text-[#8E9B82] font-manrope text-sm font-medium hover:bg-[#8E9B82]/5 transition-all"
                      >
                        <ChevronDown size={16} /> Show {hiddenCount} more observations
                      </button>
                    )}
                    {showAllObs && sorted.length > 2 && (
                      <button
                        data-testid="show-less-observations"
                        onClick={() => setShowAllObs(false)}
                        className="w-full mt-3 flex items-center justify-center gap-2 py-3 rounded-2xl bg-white border border-[#E2DFD8]/50 text-[#6B7264] font-manrope text-sm font-medium hover:bg-[#F7F5F0] transition-all"
                      >
                        <ChevronUp size={16} /> Show less
                      </button>
                    )}
                  </div>
                );
              })()}

              {/* Suggestions */}
              {summary.suggestions?.length > 0 && (
                <div className="bg-[#8E9B82]/5 rounded-[24px] p-5 border border-[#8E9B82]/10">
                  <div className="flex items-center gap-2 mb-4">
                    <Lightbulb size={18} className="text-[#D4A373]" strokeWidth={1.5} />
                    <h3 className="font-outfit text-lg font-medium text-[#2F3630]">Suggestions</h3>
                  </div>
                  <div className="space-y-3">
                    {summary.suggestions.map((s, i) => (
                      <div key={i} className="flex gap-3" data-testid={`suggestion-${i}`}>
                        <div className="w-6 h-6 rounded-full bg-[#D4A373]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs font-outfit font-medium text-[#D4A373]">{i + 1}</span>
                        </div>
                        <div>
                          <p className="font-manrope text-sm text-[#2F3630]">{s.text}</p>
                          {s.related_observation && (
                            <p className="font-manrope text-xs text-[#6B7264] mt-1 italic">
                              Based on: {s.related_observation}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Week/Month Views */}
      {period === "week" && (
        <div className="space-y-5 animate-fade-up">
          <div className="bg-white rounded-[24px] p-5 shadow-[0_4px_24px_-8px_rgba(47,54,48,0.05)] border border-[#E2DFD8]/50">
            <div className="flex items-center gap-2 mb-3">
              <CalendarDays size={18} className="text-[#D4A373]" strokeWidth={1.5} />
              <h3 className="font-outfit text-lg font-medium text-[#2F3630]">Weekly Overview</h3>
            </div>
            <p className="font-manrope text-sm text-[#6B7264] leading-relaxed">
              This was a positive week overall. Aavyaa appeared cheerful and engaged across most play and guided activity sessions. Her balance while sitting remained steady throughout the week, though walking stability varied — she seemed more confident in the mornings. Glasses compliance was mixed, with around 55-60% wearing time during face-visible hours. There were 2-3 instances daily where she removed or threw her glasses, mostly during active play. Meal cooperation was moderate across the week, with breakfast going better than lunch. The caregiver maintained attentive engagement throughout, with minimal phone usage during key interaction times.
            </p>
          </div>
          <div className="bg-white rounded-[24px] p-5 border border-[#E2DFD8]/50">
            <h3 className="font-outfit text-base font-medium text-[#2F3630] mb-3">Trends This Week</h3>
            <div className="space-y-3">
              {[
                { label: "Cheerfulness", trend: "Consistently moderate to high", direction: "stable", emoji: "Positive" },
                { label: "Glasses wearing", trend: "55-60% compliance, improving slightly", direction: "up", emoji: "Improving" },
                { label: "Balance (walking)", trend: "Steady mornings, less stable afternoons", direction: "stable", emoji: "Stable" },
                { label: "Meal cooperation", trend: "Breakfast better than lunch consistently", direction: "stable", emoji: "Pattern" },
                { label: "Instruction following", trend: "Mixed — better with one-step instructions", direction: "up", emoji: "Improving" },
                { label: "Caregiver engagement", trend: "Consistently attentive and gentle", direction: "stable", emoji: "Strong" },
              ].map((t, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-[#E2DFD8]/30 last:border-0">
                  <div>
                    <p className="font-manrope text-sm font-medium text-[#2F3630]">{t.label}</p>
                    <p className="font-manrope text-xs text-[#6B7264]">{t.trend}</p>
                  </div>
                  <span className={`text-[10px] px-2.5 py-1 rounded-full font-manrope font-medium ${
                    t.direction === "up" ? "bg-emerald-50 text-emerald-600" : "bg-[#8E9B82]/10 text-[#8E9B82]"
                  }`}>{t.emoji}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-gradient-to-br from-[#8E9B82]/5 to-[#D4A373]/5 rounded-[24px] p-5 border border-[#8E9B82]/10">
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb size={18} className="text-[#D4A373]" strokeWidth={1.5} />
              <h3 className="font-outfit text-base font-medium text-[#2F3630]">Weekly Suggestions</h3>
            </div>
            <div className="space-y-3">
              {[
                "Glasses compliance appeared to improve when a strap was used during play. Consider making the strap a consistent part of the routine.",
                "Aavyaa seemed most engaged during music and singing activities. Try incorporating songs into harder routines like dressing or transitions.",
                "Morning sessions showed better instruction-following. Consider scheduling therapy or guided activities before noon.",
                "Afternoon meals were tougher — try keeping lunch shorter and offering familiar foods first to build cooperation.",
              ].map((s, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#D4A373]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[10px] font-outfit font-semibold text-[#D4A373]">{i + 1}</span>
                  </div>
                  <p className="font-manrope text-sm text-[#2F3630] leading-relaxed">{s}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {period === "month" && (
        <div className="space-y-5 animate-fade-up">
          <div className="bg-white rounded-[24px] p-5 shadow-[0_4px_24px_-8px_rgba(47,54,48,0.05)] border border-[#E2DFD8]/50">
            <div className="flex items-center gap-2 mb-3">
              <CalendarDays size={18} className="text-[#D4A373]" strokeWidth={1.5} />
              <h3 className="font-outfit text-lg font-medium text-[#2F3630]">Monthly Overview</h3>
            </div>
            <p className="font-manrope text-sm text-[#6B7264] leading-relaxed">
              Looking at the past month, Aavyaa has shown encouraging progress in several areas. Her cheerfulness and responsiveness have remained consistently moderate to high, with particularly strong engagement during one-to-one interaction and music activities. Glasses compliance has gradually improved from around 40% in week 1 to approximately 60% by week 4, suggesting the strap strategy is working. Balance while sitting has been consistently steady, and walking stability has shown slight improvement, with fewer near-stumble moments in the last two weeks. Meal cooperation remains an area to watch — mornings continue to go better than afternoons. The caregiver has maintained excellent engagement throughout the month, with very low phone usage during interaction time.
            </p>
          </div>
          <div className="bg-white rounded-[24px] p-5 border border-[#E2DFD8]/50">
            <h3 className="font-outfit text-base font-medium text-[#2F3630] mb-3">Monthly Progress</h3>
            <div className="space-y-3">
              {[
                { label: "Glasses compliance", w1: "~40%", w4: "~60%", status: "Improving" },
                { label: "Walking balance", w1: "Somewhat unsteady", w4: "Mostly steady", status: "Improving" },
                { label: "Instruction following", w1: "Mostly ignored", w4: "Mixed (improving)", status: "Improving" },
                { label: "Cheerfulness", w1: "Moderate", w4: "Moderate-High", status: "Stable" },
                { label: "Meal cooperation", w1: "Resistant", w4: "Somewhat cooperative", status: "Improving" },
                { label: "Aggressive episodes", w1: "3-4/day", w4: "1-2/day", status: "Improving" },
              ].map((p, i) => (
                <div key={i} className="bg-[#F7F5F0] rounded-xl p-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="font-manrope text-sm font-medium text-[#2F3630]">{p.label}</p>
                    <span className="text-[10px] px-2.5 py-1 rounded-full font-manrope font-medium bg-emerald-50 text-emerald-600">{p.status}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs font-manrope text-[#6B7264]">
                    <span className="bg-white px-2 py-0.5 rounded">Week 1: {p.w1}</span>
                    <span className="text-[#8E9B82]">&rarr;</span>
                    <span className="bg-white px-2 py-0.5 rounded">Week 4: {p.w4}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-[24px] p-5 border border-[#E2DFD8]/50">
            <h3 className="font-outfit text-base font-medium text-[#2F3630] mb-2">Strongest Areas</h3>
            <div className="flex flex-wrap gap-2 mb-4">
              {["Cheerful disposition", "One-to-one engagement", "Caregiver bond", "Music responsiveness", "Sitting balance"].map(s => (
                <span key={s} className="text-xs px-3 py-1.5 rounded-full bg-[#8E9B82]/10 text-[#8E9B82] font-manrope">{s}</span>
              ))}
            </div>
            <h3 className="font-outfit text-base font-medium text-[#2F3630] mb-2">Areas to Watch</h3>
            <div className="flex flex-wrap gap-2">
              {["Afternoon meals", "Glasses during play", "Walking stability", "Multi-step instructions"].map(s => (
                <span key={s} className="text-xs px-3 py-1.5 rounded-full bg-[#D4A373]/10 text-[#D4A373] font-manrope">{s}</span>
              ))}
            </div>
          </div>
          <div className="bg-gradient-to-br from-[#8E9B82]/5 to-[#D4A373]/5 rounded-[24px] p-5 border border-[#8E9B82]/10">
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb size={18} className="text-[#D4A373]" strokeWidth={1.5} />
              <h3 className="font-outfit text-base font-medium text-[#2F3630]">Monthly Recommendations</h3>
            </div>
            <div className="space-y-3">
              {[
                "Glasses compliance is trending up — keep using the strap strategy and consider rewarding sustained wear with a preferred activity.",
                "Schedule demanding activities (therapy, instruction-based play) in the morning when engagement and cooperation are highest.",
                "Continue increasing floor play and supported walking to build on the balance improvements seen this month.",
                "Explore using music transitions (a specific song for each activity change) to reduce protest during transitions.",
                "Share the monthly progress with your therapist — the improvements in instruction-following and reduced aggression are worth discussing in the next session.",
              ].map((s, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#D4A373]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[10px] font-outfit font-semibold text-[#D4A373]">{i + 1}</span>
                  </div>
                  <p className="font-manrope text-sm text-[#2F3630] leading-relaxed">{s}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Video Player Dialog */}
      <Dialog open={!!videoClip} onOpenChange={() => setVideoClip(null)}>
        <DialogContent className="max-w-lg p-0 rounded-2xl overflow-hidden bg-black">
          {videoClip && (
            <div>
              <video
                src={videoClip.s3_url}
                controls
                autoPlay
                className="w-full max-h-[70vh]"
                data-testid="video-player"
              />
              <div className="bg-white p-3">
                <p className="font-manrope text-xs text-[#6B7264]">
                  {formatTime(videoClip.timestamp_str)} &middot; {videoClip.filename}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
}
